import { type KeyboardEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LinienSymbol } from './linien-symbol.js';
import stile from './volltext-dialog.module.css';

const FOKUSSIERBAR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Vergroessern-Dialog fuer abgeschnittenen Text, uebernommen aus der Content-Factory
 * (components/volltext-dialog/volltext-dialog.tsx): Fokus-Falle, Escape, Fokus zurueck an den
 * Ausloeser. Als Portal an `document.body`, damit kein haftender Vorfahr mit eigenem
 * Stapelkontext die Blende verdeckt (dort am 23.08.2026 gemessen).
 */
export function useVolltextDialog() {
  const [offen, setOffen] = useState<{ titel: string; inhalt: ReactNode } | null>(null);
  const dialogRef = useRef<HTMLElement>(null);
  const ausloeserRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!offen) return;
    const beiTaste = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') setOffen(null);
    };
    window.addEventListener('keydown', beiTaste);
    dialogRef.current?.querySelector<HTMLElement>(FOKUSSIERBAR)?.focus();
    return () => {
      window.removeEventListener('keydown', beiTaste);
      ausloeserRef.current?.focus();
      ausloeserRef.current = null;
    };
  }, [offen]);

  function oeffnen(titel: string, inhalt: ReactNode) {
    ausloeserRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setOffen({ titel, inhalt });
  }

  function fokusFalle(e: KeyboardEvent) {
    if (e.key !== 'Tab') return;
    const elemente = dialogRef.current?.querySelectorAll<HTMLElement>(FOKUSSIERBAR);
    const erster = elemente?.[0];
    const letzter = elemente?.[elemente.length - 1];
    if (!erster || !letzter) return;
    if (e.shiftKey && document.activeElement === erster) {
      e.preventDefault();
      letzter.focus();
    } else if (!e.shiftKey && document.activeElement === letzter) {
      e.preventDefault();
      erster.focus();
    }
  }

  const dialog = offen
    ? createPortal(
        <div className={stile.blende} role="presentation" onMouseDown={() => setOffen(null)}>
          <section
            ref={dialogRef}
            className={stile.dialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="volltext-dialog-titel"
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={fokusFalle}
          >
            <div className={stile.kopf}>
              <h2 className={stile.titel} id="volltext-dialog-titel">
                {offen.titel}
              </h2>
              <button
                type="button"
                className={stile.schliessen}
                aria-label="Schließen"
                onClick={() => setOffen(null)}
              >
                <LinienSymbol name="x" groesse={18} />
              </button>
            </div>
            <div className={stile.inhalt}>{offen.inhalt}</div>
          </section>
        </div>,
        document.body,
      )
    : null;

  return { oeffnen, dialog };
}

/** Der Knopf unten rechts am Feld; erwartet einen `position: relative`-Rahmen um sich. */
export function VergroessernKnopf({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      className={stile.vergroessern}
      aria-label={`${label} vergrößern`}
      onClick={onClick}
    >
      <LinienSymbol name="maximize" groesse={16} />
    </button>
  );
}
